import React, { useEffect, useState } from "react";
import { useTable, useSortBy, Column } from "react-table";
import copy from "copy-to-clipboard";
import { UserInfoForList, UserRow } from "../../types";
import { TableBurgerIcon } from "../../icons/tableBurger";
import Paginator from "../paginator";
import { CellSortIcon } from "../../icons/sort";
import ShortManInfo from "../ui/shortItem";
import { backend, GroupListData, InviteLinkData, Role } from "../../services/backend";
import { defPageSize } from "../../constants/events";
import { useDispatch, useSelector } from "react-redux";
import { actions, RootState } from "../../state/reducer";
import { toast } from "sonner";
import { CopyIcon } from "../../icons/copy";
import { CloseIcon } from "../../icons/close";
import { generateRandomString } from "../../utils";

interface InviteTableProps {
  columns: Column<InviteLinkData>[];
  data: InviteLinkData[];
}

const tableGridTemplate = "1fr 2fr 2fr 2fr 2fr 2fr";

const LinksTable: React.FC<InviteTableProps> = ({ columns, data }) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable<InviteTableProps>({ columns, data }, useSortBy);

  return (
    <div className="listTable" {...getTableProps()} style={{ display: "grid", gap: "10px" }}>
      {/* Заголовок таблицы */}
      <div
        className="tableHeading"
        style={{
          display: "grid",
          gridTemplateColumns: tableGridTemplate,
        }}
      >
        {headerGroups.map((headerGroup) =>
          headerGroup.headers.map((column) => (
            <div
              {...column.getHeaderProps(column.getSortByToggleProps())}
              style={{ cursor: column.canSort ? "pointer" : "default" }}
            >
              {column.render("Header")}
              {column.Header !== "" && typeof column.Header === "string" ? (
                <CellSortIcon
                  width={8}
                  height={5}
                  classNames={
                    column.isSortedDesc
                      ? ["sortedDesc", "grayFilled"]
                      : ["grayFilled"]
                  }
                />
              ) : null}
            </div>
          ))
        )}
      </div>

      {/* Тело таблицы */}
      <div {...getTableBodyProps()} style={{ display: "grid", gap: "10px" }}>
        {rows.map((row) => {
          prepareRow(row);
          return (
            <div
              {...row.getRowProps()}
              style={{
                display: "grid",
                gridTemplateColumns: tableGridTemplate,
                padding: "10px 0",
                borderBottom: "1px solid #ccc",
              }}
            >
              {row.cells.map((cell: any) => (
                <div {...cell.getCellProps()}>{cell.render("Cell")}</div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LinksListTable = (props: { search: string }) => {
  const State = useSelector((state: RootState) => state);
  const dispatch = useDispatch();
  const [displayLinks, setDisplayLinks] = useState<InviteLinkData[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [groups, setGroups] = useState<GroupListData[]>([]);

  const trigger = State.selectedItem;
  const search = props.search

  useEffect(() => {
     backend.getInviteLinksList().then((res) => {
      setDisplayLinks(res.links)
     });
     backend.getRoleList().then((res) => {
      setRoles(res.roles)
    }).catch((e) => {
      toast("Не удалось загрузить список ролей")
    })
    backend.getGroupsList().then((res) => {
      setGroups(res.groups)
    }).catch((e) => {
      toast("Не удалось загрузить список групп")
    })
  }, [trigger])

  const links = !search ? displayLinks : displayLinks.filter((link) => {
    const searchRow = Object.values(link).join(' ');
    return searchRow.indexOf(search) > -1
  })

  const columns: Column<InviteLinkData>[] = [
    { Header: "№", accessor: "id", canSort: true},
    { Header: "Ссылка", accessor: "name", Cell: ({ row }: any) => (
      <a href={`/signup/${row.original.name}`} target="_blank">Открыть</a>
    )},
    {
      Header: "Группа",
      accessor: "group_id",
      Cell: ({ row }: any) => (
        <div className="userRoleTableText">{groups.find((group) => group.id === Number(row.original.group_id))?.name || `№${row.original.group_id}`}</div>
      ),
    },
    { Header: "Роль", accessor: "role_id",
      Cell: ({ row }: any) => (
        <div className="userRoleTableText">{roles.find((role) => role.id === Number(row.original.role_id))?.name || `№${row.original.role_id}`}</div>
      ) 
    },
    { Header: "Роль в группе", accessor: "group_role_id",
      Cell: ({ row }: any) => (
        <div className={`userRoleTableText ${row.original.group_role_id === 1 ? "green" : "blue"}`}>{row.original.group_role_id === 1 ? "Владелец" : "Участник"}</div>
      ) 
    },
    { Header: "", accessor: "actions",
      Cell: ({ row }: any) => {
        return (
        <div className="flex gap6">
          <div className="aBtn iconBtn gray" onClick={() => {
            copy(`${document.location.protocol}://${document.location.hostname}/signup/${row.original.name}`)
            toast("Ссылка скопирована в буфер обмена")
            }}>
             <CopyIcon width={24} height={24} />
             <div className="hint">
                Скопировать
             </div>
          </div>
          <div className="aBtn iconBtn red" onClick={() => {
            backend.deleteInviteLink(row.original.name).then(() => {
              toast("Ссылка удалена")
              dispatch(actions.selectItem(generateRandomString(3)))
            }).catch(() => {
              toast("ошибка удаления ссылки")
            })
          }}>
             <CloseIcon width={24} height={24} />
             <div className="hint">
                Удалить
             </div>
          </div>
        </div>
      ) }
    }
  ];

  return (
    <div className="tableContainer">
      <LinksTable data={links} columns={columns} />
    </div>
  );
};

export default LinksListTable;
